/**
 * Bridge to the Photon Dial Chrome extension.
 *
 * The extension's content script listens for window.postMessage events tagged
 * `source: 'photon-dial-portal'` and forwards them to its service worker,
 * which already speaks to the loopback broker at 127.0.0.1:17999. Responses
 * come back via window.postMessage tagged `source: 'photon-dial-extension'`.
 *
 * The portal can't reach the broker directly (HTTPS → HTTP loopback is blocked
 * by mixed-content / private-network rules), so this is the only path.
 */

interface BrokerResponse {
  ok: boolean;
  error?: string;
  [key: string]: unknown;
}

export interface BrokerCallEvent {
  type: "initiated" | "ringing" | "answered" | "hung-up" | "incoming" | "incoming-enriched" | string;
  callId?: string;
  from?: string;
  to?: string;
  calledDevice?: string;
  direction?: string;
  contact?: unknown;
  portalUrl?: string;
}

export interface ActiveCallEntry {
  callId: string;
  state?: string;
  type?: string;
  from?: string;
  to?: string;
  calledDevice?: string;
  direction?: string;
  updatedAt?: number;
  [key: string]: unknown;
}

export interface BrokerStateSnapshot {
  loggedIn?: boolean;
  bound?: boolean;
  deviceId?: string | null;
  sessionId?: string | null;
}

export interface RecentCallEntry {
  callId: string;
  direction: "in" | "out" | null;
  from: string | null;
  to?: string | null;
  contact?: {
    found?: boolean;
    contactId?: string;
    name?: string | null;
    company?: string | null;
    email?: string | null;
    url?: string | null;
  } | null;
  state: "new" | "ringing" | "initiated" | "answered" | "ended";
  startedAt: number;
  answeredAt: number | null;
  endedAt: number | null;
  updatedAt?: number;
}

export type BrokerEventMessage =
  | { type: "event"; payload: { event: string; data: BrokerCallEvent } }
  | { type: "active-calls"; payload: Record<string, ActiveCallEntry> }
  | { type: "recent-calls"; payload: RecentCallEntry[] }
  | { type: "broker-state"; payload: BrokerStateSnapshot }
  | { type: "status"; payload: { connected: boolean; reason?: string } };

/**
 * Subscribe to live broker events relayed by the Photon Dial extension's
 * content script. Returns an unsubscribe function. Each handler receives
 * the same shape the extension SW broadcasts internally.
 *
 * The extension may not be installed; in that case this just never fires
 * and the caller stays in their default state.
 */
export function onBrokerEvent(handler: (msg: BrokerEventMessage) => void): () => void {
  const listener = (event: MessageEvent) => {
    if (event.source !== window) return;
    const d = event.data;
    if (!d || d.source !== "photon-dial-extension-event") return;
    if (!d.type) return;
    handler({ type: d.type, payload: d.payload });
  };
  window.addEventListener("message", listener);
  return () => window.removeEventListener("message", listener);
}

const REQUEST_TIMEOUT_MS = 5000;

function brokerCommandOnce(
  type: "dial" | "hangup" | "get-state" | "reconnect",
  payload: Record<string, unknown>,
): Promise<BrokerResponse> {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("not in a browser context"));
      return;
    }
    const requestId = crypto.randomUUID();
    let settled = false;
    const handler = (event: MessageEvent) => {
      if (event.source !== window) return;
      const d = event.data;
      if (!d || d.source !== "photon-dial-extension" || d.requestId !== requestId) return;
      if (settled) return;
      settled = true;
      window.removeEventListener("message", handler);
      const resp = (d.response as BrokerResponse) ?? null;
      if (!resp) {
        reject(new Error("empty response"));
        return;
      }
      // get-state returns a raw storage object with no `ok` field; only treat
      // an explicit `ok: false` as a failure so successful state lookups
      // resolve correctly.
      if (resp.ok === false) reject(new Error(resp.error || "broker call failed"));
      else resolve(resp);
    };
    window.addEventListener("message", handler);
    setTimeout(() => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", handler);
      reject(new Error("Photon Dial extension did not respond — is it installed and the broker running?"));
    }, REQUEST_TIMEOUT_MS);
    window.postMessage({ source: "photon-dial-portal", requestId, type, ...payload }, "*");
  });
}

export async function brokerCommand(
  type: "dial" | "hangup" | "get-state" | "reconnect",
  payload: Record<string, unknown> = {},
): Promise<BrokerResponse> {
  // Retry once: MV3 service workers can be evicted between events; the first
  // request after a nap sometimes fails with the channel-closed-before-response
  // error. The retry hits a now-awake SW and almost always succeeds. Errors
  // from a real broker problem (offline, no callId, etc.) come back as
  // {ok:false} from the SW itself and don't reject — those are surfaced via
  // the brokerCommandOnce reject and we still retry once before giving up.
  try {
    return await brokerCommandOnce(type, payload);
  } catch (err) {
    if (typeof window === "undefined") throw err;
    await new Promise((r) => setTimeout(r, 250));
    return brokerCommandOnce(type, payload);
  }
}
